/**
 * Comm Hub message "markdown" → HTML, SAFE for dangerouslySetInnerHTML (review 3.3, 2026-09-24).
 *
 * Order matters:
 *   1. HTML-escape the WHOLE input (& < > " ') — after this no user text can form a tag or
 *      leave an attribute, so the markdown pass below only ever emits our own fixed markup.
 *   2. Pull [text](url) links out into placeholders BEFORE the other rules run, so the mention
 *      and emoji rules can never inject markup inside an href.
 *   3. Only http(s) and mailto hrefs become links; anything else renders as plain text.
 */
const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ESCAPES[c]);
}

/** A URL safe to put in an href: http(s), mailto, or a same-origin absolute path. */
export function safeHref(url: string | null | undefined): string | undefined {
  if (typeof url !== 'string') return undefined;
  if (/^(https?:\/\/|mailto:)/i.test(url)) return url;
  if (/^\/(?!\/)/.test(url)) return url;
  return undefined;
}

export function renderMessageMarkdown(text: string): string {
  const links: string[] = [];
  let out = escapeHtml(String(text ?? ''))
    // Links [text](url) — url is already escaped, so it cannot contain a quote.
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (whole, label: string, url: string) => {
      if (!/^(https?:\/\/|mailto:)/i.test(url)) return whole;
      links.push(`<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-300 underline">${label}</a>`);
      return `\u0000${links.length - 1}\u0000`;
    });

  out = out
    // Bold text **text**
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic text *text*. No lookbehind: Safari < 16.4 rejects it as a syntax
    // error, and in the entry chunk that stops the whole app from booting.
    .replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>')
    // Inline code `code`
    .replace(/`([^`]+)`/g, '<code class="bg-slate-700 px-1 rounded text-amber-400">$1</code>')
    // Mentions @user
    .replace(/@(\w+)/g, '<span class="text-amber-400 font-medium">@$1</span>')
    // Emojis :emoji:
    .replace(/:(\w+):/g, '<span class="text-lg">😊</span>');

  return out.replace(/\u0000(\d+)\u0000/g, (_m, i: string) => links[Number(i)] ?? '');
}
